import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, IconButton, Stack, TextField, Typography, CircularProgress, Tooltip } from '@mui/material';
import { ReloadOutlined, SendOutlined, PaperClipOutlined, SearchOutlined } from '@ant-design/icons';
import api from 'api/axios';

// ---------- helpers ----------
function formatWaNumber(chatId) {
  if (!chatId) return '-';
  const parts = String(chatId).split('@');
  const raw = parts[0];
  if (!raw) return chatId;

  if (raw.startsWith('62')) {
    const rest = raw.slice(2);
    if (/^8\d{7,11}$/.test(rest)) {
      const a = rest.slice(0, 3);
      const b = rest.slice(3, 7);
      const c = rest.slice(7);
      if (c) return `+62 ${a}-${b}-${c}`;
      return `+62 ${a}-${b}`;
    }
    return `+${raw}`;
  }

  if (raw.startsWith('1') && raw.length >= 10 && raw.length <= 11) {
    const area = raw.slice(1, 4);
    const mid = raw.slice(4, 7);
    const last = raw.slice(7);
    return `+1 (${area}) ${mid}-${last}`;
  }

  return `+${raw}`;
}

function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function buildDataUrlFromApiMessage(m) {
  if (!m?.mediaBase64 || !m?.mediaMimeType) return null;
  return `data:${m.mediaMimeType};base64,${m.mediaBase64}`;
}

function isPlaceholderMessage(msg) {
  return !msg || msg === '[image]' || msg === '[video]' || msg === '[sticker]';
}

// normalize WS payload -> FE message shape
function normalizeWsMessage(raw) {
  // raw: { sessionName, direction, message:{ id, from, to, body, timestamp, ... } }
  const sessionName = raw?.sessionName || '';
  const direction = raw?.direction === 'outgoing' ? 'outgoing' : 'incoming';
  const m = raw?.message || {};

  const messageId = m.id || raw?.messageId || '';
  const from = m.from || raw?.fromWaId || '';
  const to = m.to || raw?.toNumber || '';

  // chatId lawan bicara: incoming => from, outgoing => to
  const chatId = direction === 'incoming' ? from : to;

  const body = m.body ?? m.text ?? m.caption ?? raw?.message ?? '';

  const tsSec = typeof m.timestamp === 'number' ? m.timestamp : typeof m.t === 'number' ? m.t : null;
  const createdAt = raw?.createdAt || m.createdAt || (tsSec ? new Date(tsSec * 1000).toISOString() : new Date().toISOString());

  const status = raw?.status || (typeof m.ack === 'number' ? String(m.ack) : '') || (m.status ? String(m.status) : '');

  return {
    id: raw?.id || messageId || Date.now(),
    messageId,
    sessionName,
    chatId,
    fromWaId: from,
    toNumber: to,
    message: body,
    direction,
    status,
    createdAt,
    source: raw?.source || 'ws'
    // NOTE: WS biasanya tidak kirim mediaBase64/mediaMimeType, nanti kebaca dari fetchMessagesForActive()
  };
}

// ---------- SessionMultiSelect (NO MUI) ----------
function SessionMultiSelect({ options, value, onChange, placeholder = 'Choose sessions...' }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selected = Array.isArray(value) ? value : [];
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const base = Array.isArray(options) ? options : [];
    if (!q) return base;
    return base.filter((x) => String(x).toLowerCase().includes(q));
  }, [options, filter]);

  const add = (name) => {
    if (!name) return;
    if (selectedSet.has(name)) return;
    onChange([...selected, name]);
  };

  const remove = (name) => {
    onChange(selected.filter((x) => x !== name));
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      {/* field */}
      <div
        onClick={() => setOpen(true)}
        style={{
          minHeight: 42,
          border: '1px solid rgba(0,0,0,0.16)',
          borderRadius: 10,
          padding: 8,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          alignItems: 'center',
          background: 'rgba(0,0,0,0.02)',
          cursor: 'text'
        }}
      >
        {selected.length === 0 ? (
          <span style={{ opacity: 0.6, fontSize: 14 }}>{placeholder}</span>
        ) : (
          selected.map((s) => (
            <span
              key={s}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '4px 10px',
                borderRadius: 999,
                background: 'rgba(0,0,0,0.06)',
                fontSize: 13
              }}
            >
              <span
                style={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
                }}
              >
                {s}
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  remove(s);
                }}
                style={{
                  border: 0,
                  background: 'transparent',
                  color: 'rgba(0,0,0,0.6)',
                  cursor: 'pointer',
                  fontSize: 14,
                  lineHeight: 1
                }}
                aria-label={`remove-${s}`}
              >
                ×
              </button>
            </span>
          ))
        )}
      </div>

      {/* dropdown */}
      {open && (
        <div
          style={{
            position: 'absolute',
            zIndex: 20,
            left: 0,
            right: 0,
            marginTop: 8,
            border: '1px solid rgba(0,0,0,0.16)',
            borderRadius: 12,
            background: '#ffffff',
            boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
            overflow: 'hidden'
          }}
        >
          <div style={{ padding: 10, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Type to filter..."
              style={{
                width: '100%',
                height: 36,
                borderRadius: 10,
                border: '1px solid rgba(0,0,0,0.16)',
                background: 'rgba(0,0,0,0.02)',
                padding: '0 12px',
                outline: 'none'
              }}
              autoFocus
            />
          </div>

          <div style={{ maxHeight: 260, overflow: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: 12, opacity: 0.7, fontSize: 14 }}>No sessions</div>
            ) : (
              filtered.map((name) => {
                const isSel = selectedSet.has(name);
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => add(name)}
                    disabled={isSel}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '10px 12px',
                      border: 0,
                      background: isSel ? 'rgba(0,0,0,0.04)' : 'transparent',
                      cursor: isSel ? 'not-allowed' : 'pointer',
                      opacity: isSel ? 0.65 : 1
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
                      }}
                    >
                      {name}
                    </span>
                  </button>
                );
              })
            )}
          </div>

          <div
            style={{
              padding: 10,
              borderTop: '1px solid rgba(0,0,0,0.08)',
              display: 'flex',
              gap: 8,
              justifyContent: 'flex-end'
            }}
          >
            <button
              type="button"
              onClick={() => {
                onChange([]);
                setFilter('');
              }}
              style={{
                border: '1px solid rgba(0,0,0,0.16)',
                background: 'transparent',
                borderRadius: 10,
                padding: '6px 10px',
                cursor: 'pointer'
              }}
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              style={{
                border: 0,
                background: 'rgba(0,0,0,0.06)',
                borderRadius: 10,
                padding: '6px 10px',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- main component ----------
export default function chat() {
  const AUTH = useMemo(
    () => ({
      withCredentials: true,
      headers: { 'X-API-Key': 'secret123' }
    }),
    []
  );

  const [sessionOptions, setSessionOptions] = useState([]);
  const [selectedSessions, setSelectedSessions] = useState([]);

  const [chats, setChats] = useState([]);
  const [chatsLoading, setChatsLoading] = useState(false);

  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  const [activeChat, setActiveChat] = useState(null);
  const [searchChat, setSearchChat] = useState('');

  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);

  // media (img / video)
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState(null);
  const mediaInputRef = useRef(null);

  // ===== WS control: hanya jalan setelah user klik "Load chats" =====
  const [wsEnabled, setWsEnabled] = useState(false);

  // ===== WS refs (biar event handler baca state terbaru) =====
  const wsRef = useRef(null);
  const wsRetryRef = useRef(0);
  const activeChatRef = useRef(null);
  const selectedSessionsRef = useRef([]);
  const wsEnabledRef = useRef(false);

  useEffect(() => {
    activeChatRef.current = activeChat;
  }, [activeChat]);

  useEffect(() => {
    selectedSessionsRef.current = selectedSessions;
  }, [selectedSessions]);

  useEffect(() => {
    wsEnabledRef.current = wsEnabled;
  }, [wsEnabled]);

  // kalau sessions kosong -> matiin WS + clear UI
  useEffect(() => {
    if (!selectedSessions.length) {
      setWsEnabled(false);
      try {
        wsRef.current?.close();
      } catch {}
      wsRef.current = null;
      wsRetryRef.current = 0;
      setChats([]);
      setActiveChat(null);
      setMessages([]);
    }
  }, [selectedSessions.length]);

  // preview URL
  useEffect(() => {
    let url;
    if (mediaFile) {
      url = URL.createObjectURL(mediaFile);
      setMediaPreviewUrl(url);
    } else {
      setMediaPreviewUrl(null);
    }
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [mediaFile]);

  // --------- fetch active sessions ----------
  const fetchActiveSessions = async () => {
    try {
      const res = await api.get('/api/sessions/active', AUTH);
      const payload = res?.data || {};
      const list = Array.isArray(payload.sessions) ? payload.sessions : [];

      const uniq = [];
      const seen = new Set();
      list.forEach((s) => {
        const name = s?.sessionName;
        if (!name || seen.has(name)) return;
        seen.add(name);
        uniq.push(name);
      });

      setSessionOptions(uniq);
    } catch (e) {
      console.error('fetch sessions list error:', e);
      setSessionOptions([]);
    }
  };

  useEffect(() => {
    fetchActiveSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ====== WS connect (scrapping backend on :3000) ======
  const connectWs = () => {
    try {
      const WS_URL = 'ws://localhost:3000/ws/messages';

      // guard: hanya connect kalau enabled & sessions sudah dipilih
      if (!wsEnabledRef.current) return;
      const picked = selectedSessionsRef.current || [];
      if (!picked.length) return;

      if (wsRef.current && (wsRef.current.readyState === 0 || wsRef.current.readyState === 1)) return;

      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        wsRetryRef.current = 0;
        console.log('[WS] connected', WS_URL);
      };

      ws.onmessage = (evt) => {
        try {
          // guard: kalau WS dimatiin / sessions kosong, skip
          if (!wsEnabledRef.current) return;
          const pickedNow = selectedSessionsRef.current || [];
          if (!pickedNow.length) return;

          const raw = JSON.parse(evt.data);
          const data = normalizeWsMessage(raw);

          // filter: hanya session yang dipilih
          if (!pickedNow.includes(data.sessionName)) return;

          // 1) upsert chats list
          setChats((prev) => {
            const list = Array.isArray(prev) ? [...prev] : [];
            const key = `${data.sessionName}|${data.chatId}`;
            const idx = list.findIndex((c) => `${c.sessionName}|${c.chatId}` === key);

            if (idx === -1) {
              list.unshift({ sessionName: data.sessionName, chatId: data.chatId });
              return list;
            }

            const [item] = list.splice(idx, 1);
            list.unshift(item);
            return list;
          });

          // 2) append message hanya untuk chat yang lagi kebuka
          const ac = activeChatRef.current;
          if (ac && ac.sessionName === data.sessionName && ac.chatId === data.chatId) {
            setMessages((prev) => {
              const list = Array.isArray(prev) ? prev : [];
              if (data.messageId && list.some((m) => m.messageId === data.messageId)) return list;
              if (!data.messageId && data.id && list.some((m) => m.id === data.id)) return list;
              return [...list, data];
            });

            // 3) kalau WS cuma ngirim marker [image]/[video], pull 1x refresh biar mediaBase64 kebaca
            if (data.message === '[image]' || data.message === '[video]' || data.message === '[sticker]') {
              setTimeout(() => {
                // masih kebuka chat yang sama?
                const ac2 = activeChatRef.current;
                if (ac2 && ac2.sessionName === data.sessionName && ac2.chatId === data.chatId) {
                  fetchMessagesForActive(ac2);
                }
              }, 700);
            }
          }
        } catch (e) {
          console.error('[WS] parse error:', e);
        }
      };

      ws.onclose = () => {
        // kalau user matiin WS, jangan reconnect
        if (!wsEnabledRef.current) return;

        const retry = wsRetryRef.current + 1;
        wsRetryRef.current = retry;

        const delay = Math.min(1000 * Math.pow(2, Math.min(retry, 5)), 15000);
        console.log('[WS] closed, retry in', delay, 'ms');

        setTimeout(() => {
          if (!wsEnabledRef.current) return;
          const pickedNow = selectedSessionsRef.current || [];
          if (!pickedNow.length) return;
          connectWs();
        }, delay);
      };

      ws.onerror = (err) => {
        console.error('[WS] error', err);
      };
    } catch (e) {
      console.error('[WS] connect error:', e);
    }
  };

  // connect WS hanya setelah wsEnabled && sessions dipilih
  useEffect(() => {
    if (!wsEnabled) {
      try {
        wsRef.current?.close();
      } catch {}
      wsRef.current = null;
      wsRetryRef.current = 0;
      return;
    }

    if (!selectedSessions.length) return;

    connectWs();

    return () => {
      try {
        wsRef.current?.close();
      } catch {}
      wsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsEnabled, selectedSessions.length]);

  // --------- fetch chats ----------
  const fetchChats = async () => {
    if (!selectedSessions.length) return;
    setChatsLoading(true);
    try {
      const qs = encodeURIComponent(selectedSessions.join(','));
      const res = await api.get(`/api/messages/chats/50/1?sessionName=${qs}`, AUTH);
      const payload = res?.data || {};
      const list = Array.isArray(payload.chats) ? payload.chats : [];

      setChats(
        list.map((c) => ({
          sessionName: c.sessionName,
          chatId: c.chatId
        }))
      );
      setActiveChat(null);
      setMessages([]);
    } catch (e) {
      console.error('fetch chats error:', e);
      setChats([]);
    } finally {
      setChatsLoading(false);
    }
  };

  // klik Load chats: enable WS baru jalan
  const handleLoadChats = async () => {
    if (!selectedSessions.length) return;
    setWsEnabled(true);
    await fetchChats();
  };

  // ====== search chat via API (endpoint: /api/messages/chats?sessionName=xxx&chatId=yyy) ======
  const [searchApiLoading, setSearchApiLoading] = useState(false);

  const resolveSessionForSearch = () => {
    // prioritas: activeChat.sessionName -> kalau cuma 1 selected -> selected[0] -> kalau banyak -> selected[0]
    if (activeChat?.sessionName) return activeChat.sessionName;
    if (selectedSessions.length) return selectedSessions[0];
    return '';
  };

  const normalizeChatIdInput = (q) => {
    const raw = String(q || '').trim();
    if (!raw) return '';
    // kalau user nulis angka saja, backend tetap bisa handle; tapi kalau butuh @c.us, biarin backend yang normalize
    return raw;
  };

  const searchChatViaApi = async () => {
    const sessionName = resolveSessionForSearch();
    const q = normalizeChatIdInput(searchChat);
    if (!sessionName || !q) return;

    setSearchApiLoading(true);
    try {
      const url = `/api/messages/chats?sessionName=${encodeURIComponent(sessionName)}&chatId=${encodeURIComponent(q)}`;
      const res = await api.get(url, AUTH);
      const payload = res?.data || {};
      const list = Array.isArray(payload.chats) ? payload.chats : [];

      if (!list.length) {
        setChats([]);
        setActiveChat(null);
        setMessages([]);
        return;
      }

      // merge: hasil search dipindah ke atas + sisanya tetap
      setChats((prev) => {
        const prevList = Array.isArray(prev) ? prev : [];
        const newOnTop = list.map((c) => ({ sessionName: c.sessionName, chatId: c.chatId }));
        const keySet = new Set(newOnTop.map((x) => `${x.sessionName}|${x.chatId}`));
        const rest = prevList.filter((x) => !keySet.has(`${x.sessionName}|${x.chatId}`));
        return [...newOnTop, ...rest];
      });
    } catch (e) {
      console.error('search chat via api error:', e);
    } finally {
      setSearchApiLoading(false);
    }
  };

  // --------- fetch messages for current active chat ----------
  const fetchMessagesForActive = async (target) => {
    const chat = target || activeChat;
    if (!chat) return;
    setMessagesLoading(true);
    try {
      const res = await api.get('/api/messages/all/200/1', AUTH);
      const payload = res?.data || {};
      const list = Array.isArray(payload.messages) ? payload.messages : [];

      const filtered = list.filter((m) => m.sessionName === chat.sessionName && m.chatId === chat.chatId);

      filtered.sort((a, b) => {
        const ta = new Date(a.createdAt).getTime();
        const tb = new Date(b.createdAt).getTime();
        return ta - tb;
      });

      setMessages(filtered);
    } catch (e) {
      console.error('fetch messages error:', e);
      setMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleSelectChat = (c) => {
    setActiveChat(c);
    fetchMessagesForActive(c);
  };

  // filter chat list by search (local filter)
  const filteredChats = useMemo(() => {
    const q = searchChat.trim().toLowerCase();
    if (!q) return chats;
    return chats.filter((c) => {
      const num = formatWaNumber(c.chatId).toLowerCase();
      return num.includes(q) || c.chatId.toLowerCase().includes(q) || c.sessionName.toLowerCase().includes(q);
    });
  }, [chats, searchChat]);

  // ambil recipients:
  const getCurrentRecipient = () => {
    if (!activeChat) return '';
    if (!messages || !messages.length) return activeChat.chatId;

    const reversed = [...messages].reverse();

    const lastIncoming = reversed.find((m) => m.direction === 'incoming' && m.fromWaId);
    if (lastIncoming) return lastIncoming.fromWaId;

    const lastOutgoing = reversed.find((m) => m.direction === 'outgoing' && m.toNumber);
    if (lastOutgoing) return lastOutgoing.toNumber;

    return activeChat.chatId;
  };

  // ---------- media handlers ----------
  const handleClickAttach = () => {
    if (!activeChat || sending) return;
    if (mediaInputRef.current) mediaInputRef.current.click();
  };

  const handleMediaChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) {
      setMediaFile(null);
      return;
    }
    setMediaFile(file);
  };

  const clearMedia = () => {
    setMediaFile(null);
    if (mediaInputRef.current) mediaInputRef.current.value = '';
  };

  // --------- send message (pakai /api/messages/bulk) ----------
  const handleSend = async () => {
    if (!activeChat) return;
    const text = inputValue.trim();
    if (!text && !mediaFile) return;

    const recipient = getCurrentRecipient();
    setSending(true);

    let fallbackTimer;

    try {
      const formData = new FormData();
      formData.append('sessionNames', activeChat.sessionName);
      formData.append('intervalSeconds', '1');
      formData.append('caption', text || '');
      formData.append('maxMessagesPerSession', '1');
      formData.append('recipients', recipient);

      if (mediaFile) {
        formData.append('image', mediaFile);
      }

      // fallback: kalau WS telat / media perlu diambil dari API
      fallbackTimer = setTimeout(() => {
        fetchMessagesForActive();
      }, 2500);

      await api.post('/api/messages/bulk', formData, AUTH);

      setInputValue('');
      clearMedia();
    } catch (e) {
      console.error('send message via bulk error:', e);
      if (fallbackTimer) clearTimeout(fallbackTimer);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDownInput = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleKeyDownSearch = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      searchChatViaApi();
    }
  };

  return (
    <Box
      sx={{
        height: 'calc(100vh - 64px)',
        maxHeight: '100vh',
        display: 'flex',
        bgcolor: '#0a1014',
        color: '#f5f5f5',
        fontFamily: '"Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
      }}
    >
      {/* LEFT: chats panel */}
      <Box
        sx={{
          width: { xs: '35%', md: 340 },
          borderRight: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* top bar - sessions */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            bgcolor: '#202c33',
            borderBottom: '1px solid rgba(0,0,0,0.6)'
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
            Sessions
          </Typography>

          <SessionMultiSelect
            options={sessionOptions}
            value={selectedSessions}
            onChange={setSelectedSessions}
            placeholder="Pilih sessions aktif..."
          />

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
            <Button size="small" variant="contained" onClick={handleLoadChats} disabled={!selectedSessions.length || chatsLoading}>
              {chatsLoading ? 'Loading…' : 'Load chats'}
            </Button>

            <Tooltip title="Refresh sessions">
              <IconButton size="small" onClick={fetchActiveSessions} sx={{ color: '#e9edef' }}>
                <ReloadOutlined style={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>

            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              {selectedSessions.length ? `Selected: ${selectedSessions.length}` : 'No session selected'}
            </Typography>
          </Stack>
        </Box>

        {/* search chats */}
        <Box
          sx={{
            px: 1.5,
            py: 1,
            bgcolor: '#111b21',
            borderBottom: '1px solid rgba(255,255,255,0.08)'
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              fullWidth
              variant="outlined"
              placeholder="Search number"
              value={searchChat}
              onChange={(e) => setSearchChat(e.target.value)}
              onKeyDown={handleKeyDownSearch}
              InputProps={{
                sx: {
                  fontSize: 13,
                  bgcolor: '#202c33',
                  borderRadius: '999px',
                  color: '#e9edef', // <-- text putih
                  '& input': { color: '#e9edef' }, // <-- pastiin input putih
                  '& input::placeholder': { color: 'rgba(233,237,239,0.55)', opacity: 1 },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.16)' }
                }
              }}
            />

            <Tooltip title="Search via API">
              <span>
                <IconButton
                  size="small"
                  onClick={searchChatViaApi}
                  disabled={searchApiLoading || !searchChat.trim() || !selectedSessions.length}
                  sx={{ color: '#e9edef' }}
                >
                  {searchApiLoading ? (
                    <CircularProgress size={16} sx={{ color: '#e9edef' }} />
                  ) : (
                    <SearchOutlined style={{ fontSize: 16 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          <Typography variant="caption" sx={{ display: 'block', mt: 0.75, opacity: 0.65 }}>
            API session: {activeChat?.sessionName || selectedSessions[0] || '-'}
          </Typography>
        </Box>

        {/* chats list */}
        <Box sx={{ flex: 1, overflowY: 'auto', bgcolor: '#111b21' }}>
          {chatsLoading && (
            <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!chatsLoading && !chats.length && (
            <Typography variant="body2" sx={{ p: 2, opacity: 0.7 }}>
              Pilih session dan klik &quot;Load chats&quot; untuk melihat nomor yang ngechat.
            </Typography>
          )}

          {!chatsLoading &&
            filteredChats.map((c) => {
              const isActive = activeChat && activeChat.chatId === c.chatId && activeChat.sessionName === c.sessionName;
              const label = formatWaNumber(c.chatId);
              return (
                <Box
                  key={`${c.sessionName}|${c.chatId}`}
                  onClick={() => handleSelectChat(c)}
                  sx={{
                    px: 2,
                    py: 1.25,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: isActive ? '#202c33' : 'transparent',
                    '&:hover': { bgcolor: '#202c33' },
                    borderBottom: '1px solid rgba(255,255,255,0.04)'
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: '#00a884',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      fontSize: 18,
                      fontWeight: 600
                    }}
                  >
                    {label.replace('+', '').charAt(0)}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" noWrap sx={{ fontSize: 14, color: '#e9edef', mb: 0.25 }}>
                      {label}
                    </Typography>
                    <Typography variant="caption" noWrap sx={{ opacity: 0.7, fontSize: 11 }}>
                      {c.chatId} <span style={{ opacity: 0.7 }}>• {c.sessionName}</span>
                    </Typography>
                  </Box>
                </Box>
              );
            })}
        </Box>
      </Box>

      {/* RIGHT: chat area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: '#0b141a' }}>
        {/* header */}
        <Box
          sx={{
            height: 60,
            px: 2,
            bgcolor: '#202c33',
            borderBottom: '1px solid rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          {activeChat ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    bgcolor: '#008069',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 16,
                    fontWeight: 600
                  }}
                >
                  {formatWaNumber(activeChat.chatId).replace('+', '').charAt(0)}
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#e9edef' }}>
                    {formatWaNumber(activeChat.chatId)}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    chatId: {activeChat.chatId} • session: {activeChat.sessionName}
                  </Typography>
                </Box>
              </Box>

              <Stack direction="row" spacing={1}>
                <Tooltip title="Refresh messages">
                  <IconButton size="small" sx={{ color: '#e9edef' }} onClick={() => fetchMessagesForActive()}>
                    <ReloadOutlined style={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </>
          ) : (
            <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>
              Pilih chat di panel kiri untuk melihat isi pesan.
            </Typography>
          )}
        </Box>

        {/* messages list */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: 3,
            py: 2,
            backgroundImage: 'radial-gradient(circle at 0 0, #18252f, #0b141a)'
          }}
        >
          {messagesLoading && (
            <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={26} />
            </Box>
          )}

          {!messagesLoading && activeChat && !messages.length && (
            <Typography variant="body2" sx={{ mt: 2, textAlign: 'center', opacity: 0.7 }}>
              Belum ada pesan untuk chat ini.
            </Typography>
          )}

          {!activeChat && (
            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="h6" sx={{ color: '#8696a0', textAlign: 'center', maxWidth: 320 }}>
                Selamat datang di panel Chat.
                <br />
                Pilih session & nomor di sisi kiri untuk mulai membaca dan membalas pesan.
              </Typography>
            </Box>
          )}

          {activeChat &&
            messages.map((m) => {
              const isOutgoing = m.direction === 'outgoing';
              const align = isOutgoing ? 'flex-end' : 'flex-start';
              const bg = isOutgoing ? '#005c4b' : '#202c33';
              const radius = isOutgoing ? '16px 0 16px 16px' : '0 16px 16px 16px';

              const mediaSrc = buildDataUrlFromApiMessage(m);
              const mime = (m.mediaMimeType || '').toLowerCase();
              const isImg = !!mediaSrc && mime.startsWith('image/');
              const isVideo = !!mediaSrc && mime.startsWith('video/');

              let displayMessage = m.message;
              if (!displayMessage || displayMessage === '[image]') displayMessage = '📷 [image]';
              if (displayMessage === '[video]') displayMessage = '🎥 [video]';
              if (displayMessage === '[sticker]') displayMessage = '🩹 [sticker]';

              const showCaptionText = !isPlaceholderMessage(m.message);

              return (
                <Box key={m.messageId || m.id} sx={{ display: 'flex', justifyContent: align, mb: 0.75 }}>
                  <Box
                    sx={{
                      maxWidth: '75%',
                      bgcolor: bg,
                      borderRadius: radius,
                      px: 1.25,
                      py: 0.75,
                      boxShadow: '0 1px 0 rgba(0,0,0,0.3)',
                      position: 'relative'
                    }}
                  >
                    {/* MEDIA FROM API */}
                    {isImg && (
                      <img
                        src={mediaSrc}
                        alt="attachment"
                        style={{
                          display: 'block',
                          width: '100%',
                          maxWidth: 360,
                          height: 'auto',
                          borderRadius: 12,
                          marginBottom: showCaptionText ? 8 : 0
                        }}
                      />
                    )}

                    {isVideo && (
                      <video
                        src={mediaSrc}
                        controls
                        style={{
                          display: 'block',
                          width: '100%',
                          maxWidth: 360,
                          borderRadius: 12,
                          marginBottom: showCaptionText ? 8 : 0
                        }}
                      />
                    )}

                    {/* TEXT */}
                    {(!mediaSrc || showCaptionText) && (
                      <Typography
                        sx={{
                          fontSize: 14,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          color: '#e9edef'
                        }}
                      >
                        {mediaSrc && showCaptionText ? m.message : displayMessage}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.3, gap: 0.5 }}>
                      <Typography variant="caption" sx={{ fontSize: 10, opacity: 0.7 }}>
                        {formatTime(m.createdAt)}
                      </Typography>
                      {isOutgoing && (
                        <Typography variant="caption" sx={{ fontSize: 10, opacity: 0.7 }}>
                          {m.status}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
        </Box>

        {/* attachment preview - DI ATAS CHATBOX */}
        {mediaFile && mediaPreviewUrl && (
          <Box
            sx={{
              borderTop: '1px solid #202c33',
              bgcolor: '#202c33',
              px: 2,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <Box
              sx={{
                width: 96,
                height: 64,
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: '#111b21',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {mediaFile.type.startsWith('image/') ? (
                <img src={mediaPreviewUrl} alt="preview" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }} />
              ) : (
                <video src={mediaPreviewUrl} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }} muted />
              )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" sx={{ fontSize: 12, color: '#e9edef' }} noWrap title={mediaFile.name}>
                {mediaFile.name}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: 11, opacity: 0.7 }}>
                {(mediaFile.size / 1024 / 1024).toFixed(2)} MB
              </Typography>
            </Box>
            <button
              type="button"
              onClick={clearMedia}
              style={{ border: 0, background: 'transparent', color: '#ff7777', cursor: 'pointer', fontSize: 12 }}
            >
              remove
            </button>
          </Box>
        )}

        {/* input bar / chatbox */}
        <Box sx={{ borderTop: '1px solid #202c33', bgcolor: '#202c33', px: 2, py: 1.25 }}>
          {/* hidden file input */}
          <input ref={mediaInputRef} type="file" style={{ display: 'none' }} accept="image/*,video/*" onChange={handleMediaChange} />

          <Stack direction="row" spacing={1.5} alignItems="center">
            {/* attach button */}
            <Tooltip title="Attach image / video">
              <span>
                <IconButton onClick={handleClickAttach} disabled={!activeChat || sending} sx={{ color: mediaFile ? '#00a884' : '#e9edef' }}>
                  <PaperClipOutlined />
                </IconButton>
              </span>
            </Tooltip>

            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder={activeChat ? `Ketik pesan ke ${formatWaNumber(activeChat.chatId)}…` : 'Pilih chat dulu'}
              disabled={!activeChat || sending}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDownInput}
              InputProps={{
                sx: {
                  bgcolor: '#2a3942',
                  borderRadius: '24px',
                  fontSize: 14,
                  color: '#e9edef',
                  '& input': { color: '#e9edef' },
                  '& input::placeholder': { color: 'rgba(233,237,239,0.55)', opacity: 1 },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.16)' }
                }
              }}
            />

            <IconButton
              disabled={!activeChat || (!inputValue.trim() && !mediaFile) || sending}
              onClick={handleSend}
              sx={{ bgcolor: '#00a884', '&:hover': { bgcolor: '#00a884cc' } }}
            >
              {sending ? (
                <CircularProgress size={20} sx={{ color: '#ffffff' }} />
              ) : (
                <SendOutlined style={{ color: '#ffffff', fontSize: 18 }} />
              )}
            </IconButton>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
